import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import {
  icpInterviewCostLabel,
  icpReadingEstimate,
  jevReadingMicroUsdOf,
  type IcpInterviewView,
  type IcpItem,
  type IcpItemRequest,
  type IcpProfileView,
  type SaveIcpRequest,
} from '@nest-recruit/shared';
import { apiGet, errorMessage } from '../../lib/api-client';
import { useToasts } from '../../stores/toasts';
import { interviewKey, useDraftFromRequest, useIcp, useLatestInterview, useInterviewActions, useSaveIcp, type InterviewStep } from './api/icp-api';
import { estimateCostOf, itemInputOf, itemOfInput, mergeDraftIntoForm } from './icp-form';

// ADR 0062 §4, mockup M1: the ideal candidate inside job setup. The same endpoints and the same interview as Ranking's docked
// panel, but the section is one place: empty, the interview, or the form. What is typed and not yet saved is the `draft`; it
// survives the interview closing, so nothing typed is lost, and "Open job" asks about it (it can spend money to save).

export type SetupPanel = 'interview' | 'form';

/** The lines on screen that are not the saved profile: a draft from the interview, or the recruiter's unsaved edits. */
export interface SetupDraft {
  readonly items: readonly IcpItem[];
  readonly interviewId: string | null;
  readonly notes: readonly string[];
}

export interface IcpSetup {
  readonly isLoading: boolean;
  readonly hasError: boolean;
  readonly retry: () => void;
  readonly profile: IcpProfileView | null;
  /** The lines as they stand on screen: the draft, else the saved profile; null before anything exists. */
  readonly items: readonly IcpItem[] | null;
  readonly isJevConnected: boolean;
  /** Which of the three the section shows: nothing built yet (null), the interview, or the form. */
  readonly panel: SetupPanel | null;
  readonly draft: SetupDraft | null;
  /** A new draft remounts the form, so its fields start from the new lines; saving does not, so what is typed never blinks. */
  readonly formKey: number;
  readonly isUnsaved: boolean;
  readonly interview: IcpInterviewView | null | undefined;
  readonly interviewStep: InterviewStep | null;
  readonly interviewCost: string | null;
  /** "about 3¢" for Save and rank, or null when there are no CVs yet to read. */
  readonly saveCost: (items: readonly IcpItemRequest[]) => string | null;
  /** What Save and rank is expected to cost, in millionths of a dollar, for the Jev budget's words; 0 with no CVs to read. */
  readonly saveMicroUsd: (items: readonly IcpItemRequest[]) => number;
  readonly build: () => void;
  readonly write: () => void;
  readonly stop: () => void;
  readonly startInterview: () => void;
  readonly answer: (turn: number, answer: { readonly choices: readonly string[]; readonly text: string | null; readonly isSkipped: boolean }) => void;
  readonly nextQuestion: () => void;
  readonly draftNow: () => void;
  /** ADR 0075: the rated lines drafted from the hire request's ideal-candidate text, into the form, unsaved. */
  readonly fromRequest: () => void;
  readonly isDraftingFromRequest: boolean;
  readonly editDraft: (items: readonly IcpItem[]) => void;
  readonly askMore: (items: readonly IcpItemRequest[]) => void;
  /** Saves the lines (the profile, ranking the CVs already in). Resolves true when the server took them. */
  readonly save: (items: readonly IcpItemRequest[]) => Promise<boolean>;
  /** Saves what is unsaved on screen, for "Open job"; true when there was nothing to save or it was saved. */
  readonly saveUnsaved: () => Promise<boolean>;
}

interface SetupInput {
  readonly jobId: string;
  /** CVs already in the job (the ranking's count): the cost of "Save and rank" is worked out over them. */
  readonly cvCount: number;
  /** Called once the server has the profile, so the job's setup tick can refresh. */
  readonly onSaved: () => Promise<void>;
}

export function useIcpSetup({ jobId, cvCount, onSaved }: SetupInput): IcpSetup {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const query = useIcp(jobId, true);
  const view = query.data;
  const profile = view?.profile ?? null;

  const [panel, setPanel] = useState<SetupPanel | null>(null);
  const [draft, setDraft] = useState<SetupDraft | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [isOpening, setOpening] = useState(false);
  const latest = useLatestInterview(jobId, panel === 'interview');
  const actions = useInterviewActions(jobId);
  const requestDraft = useDraftFromRequest(jobId);
  const interview = latest.data;
  const settle = useRef<((isSaved: boolean) => void) | null>(null);

  const shownItems = draft?.items ?? profile?.items ?? null;
  const effectivePanel = panel ?? (profile || draft ? 'form' : null);

  const startFresh = async (mode: 'new' | 'more', lines: readonly IcpItemRequest[]): Promise<void> => {
    await actions.start({ mode, items: [...lines] });
  };

  /** An interview not yet drafted is picked up where it was left; otherwise a new one starts. */
  const build = (): void => {
    setPanel('interview');
    setOpening(true);
    void (async (): Promise<void> => {
      try {
        const last = await queryClient.fetchQuery<IcpInterviewView | null>({
          queryKey: interviewKey(jobId),
          queryFn: () => apiGet<IcpInterviewView | null>(`/jobs/${jobId}/ranking/icp/interviews/latest`),
          staleTime: 0,
        });
        if (last?.status === 'asking' && last.mode === 'new') return;
        await startFresh('new', []);
      } catch (error) {
        show({ tone: 'danger', message: 'The interview didn’t start', detail: errorMessage(error) });
      } finally {
        setOpening(false);
      }
    })();
  };

  const askMore = (lines: readonly IcpItemRequest[]): void => {
    setDraft({ items: lines.map(itemOfInput), interviewId: draft?.interviewId ?? null, notes: [] });
    setPanel('interview');
    void startFresh('more', lines);
  };

  const draftNow = (): void => {
    if (!interview) return;
    void (async (): Promise<void> => {
      const done = await actions.draft(interview.id);
      if (!done?.draft) return;
      const lines = done.mode === 'more' ? mergeDraftIntoForm(shownItems ?? [], done.draft.items) : done.draft.items;
      setDraft({ items: lines, interviewId: done.id, notes: done.draft.notes });
      setFormKey((key) => key + 1);
      setPanel('form');
    })();
  };

  const fromRequest = (): void => {
    void (async (): Promise<void> => {
      const done = await requestDraft.draftFromRequest();
      if (!done?.draft) return;
      setDraft({ items: done.draft.items, interviewId: done.id, notes: done.draft.notes });
      setFormKey((key) => key + 1);
      setPanel('form');
    })();
  };

  // A refusal brings the form back with the words as they were (a 409 is someone else's save).
  const { save: saveProfile } = useSaveIcp(jobId, {
    onSaved: () => {
      void onSaved().then(() => {
        settle.current?.(true);
        settle.current = null;
      });
    },
    onFailed: (input) => {
      setDraft({ items: input.items.map(itemOfInput), interviewId: input.interviewId ?? null, notes: [] });
      setFormKey((key) => key + 1);
      setPanel('form');
      settle.current?.(false);
      settle.current = null;
    },
  });

  const save = (lines: readonly IcpItemRequest[]): Promise<boolean> => {
    const request: SaveIcpRequest = { version: profile?.version ?? null, items: [...lines], interviewId: draft?.interviewId ?? null, rank: true };
    return new Promise<boolean>((resolve) => {
      settle.current = resolve;
      setPanel('form');
      setDraft(null);
      void saveProfile(request);
    });
  };

  const saveUnsaved = async (): Promise<boolean> => {
    if (draft === null) return true;
    return save(draft.items.map(itemInputOf));
  };

  const readsCvs = cvCount > 0;
  return {
    isLoading: query.isPending,
    hasError: query.isError && !view,
    retry: () => void query.refetch(),
    profile,
    items: shownItems,
    isJevConnected: view?.isJevConnected ?? true,
    panel: effectivePanel,
    draft,
    formKey,
    isUnsaved: draft !== null,
    interview,
    interviewStep: isOpening ? 'start' : actions.step,
    interviewCost: interview ? icpInterviewCostLabel(interview.tokens) : null,
    saveCost: (lines) => (readsCvs ? estimateCostOf(icpReadingEstimate(lines.map(itemOfInput), null, cvCount).label) : null),
    saveMicroUsd: (lines) => (readsCvs ? jevReadingMicroUsdOf(cvCount, lines.filter((line) => line.kind === 'jev').length) : 0),
    build,
    write: () => setPanel('form'),
    stop: () => setPanel(null),
    startInterview: () => void startFresh('new', []),
    answer: (turn, answer) => {
      if (interview) void actions.answer(interview, { turn, choices: [...answer.choices], text: answer.text, isSkipped: answer.isSkipped });
    },
    nextQuestion: () => {
      if (interview) void actions.next(interview.id);
    },
    draftNow,
    fromRequest,
    isDraftingFromRequest: requestDraft.isDrafting,
    editDraft: (lines) => setDraft((previous) => ({ items: lines, interviewId: previous?.interviewId ?? null, notes: previous?.notes ?? [] })),
    askMore,
    save,
    saveUnsaved,
  };
}
